import jwt, { JwtPayload } from 'jsonwebtoken';

import { APIResponse } from '../../../system-libraries/api-types';
import { composeHeadersWithAuthorizationToken, composeHeaders } from '../../../system-utils/api-utils/api-headers';
import { withResponseHandler } from '../../../system-utils/api-utils/response-handlers';
import { constructGetLongTermTokenApiUrl, constructGetPublicKeyApiUrl } from './urls';

const additionalHeaders: Record<string, string> = {
  'x-bpi-service-context': 'USER', // There is a comment in repo api-connector-lib src/config.js saying 'todo confirm this'
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  'X-TS-AJAX-Request': true, // This header is added in repo api-connector-lib src/config.js
};

/**
 * Exchanges short_access_token for a long term access_token
 * @param shortAccessToken
 * @returns
 */
export const postExchangeAccessToken = async (shortAccessToken: string): Promise<APIResponse<{ access_token: string }>> => {
  const headers = composeHeadersWithAuthorizationToken(shortAccessToken, {
    ...additionalHeaders,
    'Content-Type': 'application/x-www-form-urlencoded', // This header is added in repo api-connector-lib src/services/oauth-services/methods/exchange-access-token/exchange-access-token.js
  });
  const bankId = 'CB';
  const longTermTokenResp = await withResponseHandler<string>(() =>
    fetch(constructGetLongTermTokenApiUrl(bankId), {
      method: 'POST',
      headers,
      // This body is taken from repo internet-banking-webapp src/js/app.jsx
      body: new URLSearchParams({
        grant_type: 'client_credentials',
        scope: '1',
      }).toString(),
    }),
  );

  let accessToken: string = null;

  if (longTermTokenResp.status !== 200) {
    return { ...longTermTokenResp, data: { access_token: accessToken } };
  }

  const longTermToken = longTermTokenResp.data;

  // Decode Long Term Token and get access_token
  const publicKeyResp = await getPublicKey();
  if (publicKeyResp.status !== 200) {
    return { ...publicKeyResp, data: { access_token: accessToken } };
  }
  if (publicKeyResp.status === 200) {
    const publicKey = publicKeyResp.data?.public_key;
    if (publicKey) {
      // This has been taken from repo internet-banking-webapp src/js/app.jsx
      const decodedJWT = await validateAuthToken(longTermToken, publicKey);
      if (typeof decodedJWT !== 'string' && decodedJWT.error) {
        return { status: 400, error: true, message: decodedJWT.error };
      }
      if (decodedJWT['jti']) {
        accessToken = decodedJWT['jti'];
      } else {
        return { status: 400, error: true, message: 'Could not retrieve token from response' };
      }
    }
  }

  return { status: 200, data: { access_token: accessToken } };
};

const getPublicKey = async () => {
  const headers = composeHeaders(additionalHeaders);
  return withResponseHandler<{ public_key: string }>(() =>
    fetch(constructGetPublicKeyApiUrl(), {
      method: 'GET',
      headers,
    }),
  );
};

// This method has been taken from repo web-lib-application-essentials src/jwtUtil/index.js
const validateAuthToken = async (jwtToken: string, publicKey: string): Promise<JwtPayload | string | { error: string }> => {
  try {
    // Public key needs to be in PEM format for the jsonwebtoken lib so this is why we have the following
    const key = `-----BEGIN PUBLIC KEY-----${publicKey}-----END PUBLIC KEY-----`;

    return jwt.verify(jwtToken, key);
  } catch (error) {
    return { error: error.message };
  }
};
